/**
 * Die Bestellungen eines Auftrags – eine Karte je Bestellung.
 *
 * Der Verlauf steht oben im Kopfblock, als Raster mit einer Zeile je
 * Bestellung; die Karten hier haben keine eigene Schrittkette mehr – das war
 * die dritte Zeitleiste, die niemand mit den anderen beiden zusammenbrachte.
 * Jede Karte hat zwei Zonen: Kopf (Nummer, Lieferant, Umfang; rechts Stand-
 * Pille, roter Knopf für den nächsten Schritt, Stift) und Fuß (Liefertermin ·
 * Verantwortlich · Zuletzt quittiert).
 *
 * Offene Punkte stehen an einer Stelle, nicht zwischen den Zeilen: eine gelbe
 * Box über den Karten listet, was fehlt oder hakt – je Zeile Ort, Sache und
 * der Weg dorthin. Die Karten bleiben dadurch gleich hoch und ruhig.
 *
 * Nichts wird hier getippt: Quittiert wird im Dialog, geändert über den
 * Stift, und beides steht in der Chronik. Der Angebotsstatus folgt der
 * hintersten Bestellung – er wird hier nicht gesetzt, er ergibt sich.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconStift } from './Icons';
import { Serviceblock } from './Serviceblock';
import { SchrittModal } from '../modals/SchrittModal';
import { MontageDialog } from '../modals/MontageDialog';
import { BestellungModal } from '../modals/BestellungModal';
import { ZustaendigModal } from '../modals/ZustaendigModal';
import { LiegtBei } from './LiegtBei';
import type { Angebot, AngebotAntwort, Bestellung } from '../typen';
import './abwicklungsblock.css';

/** Eine Zeile der gelben Box: wo, was, und wohin der Klick führt. */
interface OffenerPunkt {
  ort: string;
  sache: string;
  /** Blockiert das Quittieren – im Unterschied zu einem bloßen Hinweis. */
  blockiert: boolean;
  aktion?: { label: string; tun: () => void };
}

export function Abwicklungsblock({ angebot, onFertig }: { angebot: Angebot; onFertig: () => void }) {
  const meta = useMeta();
  const { toastZeigen } = useKora();
  const { schritte } = meta.abwicklung;
  const [schrittFuer, setSchrittFuer] = useState<Bestellung | null>(null);
  /** „Montage planen" öffnet den Montage-Dialog statt des Schritt-Dialogs. */
  const [montageOffen, setMontageOffen] = useState(false);
  const [bearbeiten, setBearbeiten] = useState<Bestellung | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  /* Eine Bestellung an jemand anderen übergeben – für ihren aktuellen Schritt. */
  const [uebergabeFuer, setUebergabeFuer] = useState<Bestellung | null>(null);

  const abgeschlossen = angebot.status === 'Abgenommen';
  /* Ohne Auftragshöhe lässt sich kein Schritt quittieren – sie gehört zu „Auftrag erteilt". */
  const auftragFehlt = !angebot.auftrag;
  const bestellungen = angebot.bestellungen;
  const lieferanten = new Set(bestellungen.map((b) => b.lieferant.trim()).filter(Boolean)).size;

  const antwort = (a: AngebotAntwort) => {
    onFertig();
    if (a.toast) toastZeigen(a.toast);
    else if (!a.sync.ok && a.sync.fehler) toastZeigen(a.sync.fehler);
  };
  const fehlerZeigen = (f: unknown) => toastZeigen(f instanceof Error ? f.message : 'Das hat nicht geklappt.');

  const entfernen = useMutation({
    mutationFn: (id: number) => api.loesche<AngebotAntwort>(`/abwicklung/bestellungen/${id}`),
    onSuccess: antwort,
    onError: fehlerZeigen,
  });

  const maengelErledigt = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/abwicklung/angebote/${angebot.id}/maengel-erledigt`),
    onSuccess: antwort,
    onError: fehlerZeigen,
  });

  /*
   * Die offenen Punkte, an einer Stelle gesammelt (Richtlinie 3): die
   * Auftragshöhe, fehlende Pflichtangaben je Bestellung (nur beim Altbestand
   * übernommen), Terminhinweise. Blockierendes zuerst.
   */
  const offene: OffenerPunkt[] = [];
  if (!abgeschlossen) {
    /*
     * Ohne Kalkulant liegt jede Bestellung bei niemandem – sie steht dann in
     * keinem Arbeitsvorrat. Das ist der teuerste stille Fehler, deshalb steht
     * er ganz oben und führt direkt zur Kundenkarte.
     */
    if (!angebot.kalkulant) {
      offene.push({
        ort: 'Auftrag',
        sache:
          'Kein Kalkulant hinterlegt – die Bestellungen liegen dadurch bei niemandem und tauchen in keinem „Mein Tag" auf.',
        blockiert: true,
        aktion: {
          label: 'Kalkulant wählen',
          tun: () =>
            document.getElementById('kalkulant')?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        },
      });
    }
    if (auftragFehlt) {
      offene.push({
        ort: 'Auftrag',
        sache: 'Auftragshöhe fehlt – ohne sie lässt sich kein Schritt quittieren. Rechts im Vorgang eintragen.',
        blockiert: true,
      });
    }
    for (const b of bestellungen) {
      const ort = `Bestellung ${b.nr}${b.lieferant ? ` · ${b.lieferant}` : ''}`;
      if (b.fehlt.length > 0) {
        offene.push({
          ort,
          sache: `Aus Monday übernommen – nachzutragen: ${b.fehlt.join(', ')}`,
          blockiert: true,
          aktion: { label: 'Ergänzen', tun: () => setBearbeiten(b) },
        });
      }
      if (b.haenger) {
        offene.push({
          ort,
          sache: [b.lieferText !== 'kein Termin' ? b.lieferText : '', b.lieferBis, b.haenger.text]
            .filter(Boolean)
            .join(' · '),
          blockiert: false,
          aktion: {
            label: 'Zur Bestellung',
            tun: () => document.getElementById(`bestellung-${b.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          },
        });
      }
    }
  }
  const blockierte = offene.filter((o) => o.blockiert).length;

  const schlagzeile =
    bestellungen.length === 0
      ? 'Noch keine Bestellung'
      : bestellungen.length === 1
        ? `1 Bestellung${lieferanten ? ' bei 1 Lieferanten' : ''}`
        : `${bestellungen.length} Bestellungen bei ${lieferanten} Lieferanten`;

  return (
    <div className="ab" id="abwicklung">
      <div className="ab-kopf">
        <div>
          <div className="ab-kicker">Bestellungen</div>
          <div className="ab-schlagzeile">
            {schlagzeile}
            {abgeschlossen && <span className="ab-schlagzeile-zusatz"> · Auftrag abgenommen</span>}
          </div>
        </div>
        {!abgeschlossen && (
          <button type="button" className="ab-knopf" onClick={() => setNeuOffen(true)}>
            + Bestellung
          </button>
        )}
      </div>

      {offene.length > 0 && (
        <div className="ab-offene">
          <div className="ab-offene-kopf">
            <b>
              {offene.length === 1 ? '1 offener Punkt' : `${offene.length} offene Punkte`}
            </b>
            <span>
              {blockierte === 0
                ? 'Hinweise – nichts blockiert.'
                : blockierte === 1
                  ? 'Ein Punkt blockiert das Quittieren.'
                  : `${blockierte} Punkte blockieren das Quittieren.`}
            </span>
          </div>
          {offene.map((o, i) => (
            <div key={i} className="ab-offene-zeile">
              <span className="ab-offene-ort">{o.ort}</span>
              <span className="ab-offene-sache">{o.sache}</span>
              {o.aktion ? (
                <button type="button" className="ab-offene-aktion" onClick={o.aktion.tun}>
                  {o.aktion.label}
                </button>
              ) : (
                <span />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Mängel aus dem Übergabeprotokoll – Altbestand vor dem Service-Modul. */}
      {angebot.maengel && (
        <div className={`ab-maengel ${angebot.maengelOffen ? 'offen' : ''}`}>
          <div className="ab-maengel-text">
            <span className="ab-maengel-titel">
              {angebot.maengelOffen ? 'Mängel offen' : 'Mängel erledigt'}
              {angebot.maengelErledigtAm ? ` · ${angebot.maengelErledigtAm.split('-').reverse().join('.')}` : ''}
            </span>
            <span>{angebot.maengel}</span>
          </div>
          {angebot.maengelOffen && (
            <button
              type="button"
              className="ab-knopf ab-knopf--prim"
              disabled={maengelErledigt.isPending}
              onClick={() => maengelErledigt.mutate()}
            >
              Mängel erledigt
            </button>
          )}
        </div>
      )}

      <div className="ab-zweig">
        {bestellungen.map((b) => {
          const letzter = b.eintraege[0];
          const loeschbar = !abgeschlossen && b.eintraege.length === 0 && bestellungen.length > 1;
          /* Der Knopf sagt, was als Nächstes anliegt – nicht „Schritt quittieren". */
          const naechster = schritte[b.schrittIndex + 1];
          const fertig = b.schritt === 'fertig';
          /*
           * Pflichtangaben können nur beim Altbestand fehlen (Richtlinie 4):
           * neue Bestellungen entstehen nur über den Dialog. Für diese Fälle
           * heißt die Karte „aus Monday übernommen", der Knopf ist gesperrt.
           */
          const uebernommen = b.fehlt.length > 0;
          const gesperrt = uebernommen || auftragFehlt;
          return (
            <article key={b.id} id={`bestellung-${b.id}`} className="ab-karte">
              <header className="ab-karte-kopf">
                <span className="ab-quadrat">{b.nr}</span>
                <div className="ab-karte-titel">
                  <div className={`ab-lieferant ${uebernommen ? 'uebernommen' : ''}`}>
                    {uebernommen
                      ? `Bestellung ${b.nr} · aus Monday übernommen${b.lieferant ? ` · ${b.lieferant}` : ''}`
                      : b.lieferant}
                  </div>
                  <div className="ab-umfang">{b.umfang || 'Bestellumfang offen'}</div>
                </div>
                <div className="ab-karte-rechts">
                  <span className={`ab-pille ${fertig ? 'fertig' : ''}`} title="Schritt und Liegezeit">
                    <span className="ab-pille-punkt" />
                    <b>{b.schrittLang}</b>
                    <span className="ab-pille-seit">
                      seit {b.tageImSchritt} {b.tageImSchritt === 1 ? 'Tag' : 'Tagen'}
                    </span>
                  </span>
                  {!abgeschlossen && naechster && (
                    <button
                      type="button"
                      className="ab-knopf-rot"
                      disabled={gesperrt}
                      title={
                        uebernommen
                          ? 'Erst möglich, wenn die Pflichtangaben ergänzt sind'
                          : auftragFehlt
                            ? 'Erst möglich, wenn die Auftragshöhe eingetragen ist'
                            : `Als Nächstes: „${naechster.aktion}" – öffnet den Dialog zum Quittieren`
                      }
                      onClick={() => {
                        /*
                         * „Montage terminiert" wird nicht im Schritt-Dialog
                         * quittiert, sondern im Montagekalender eingetragen
                         * (MONTAGEKALENDER.md 3.1 und 5.3): Dort stehen
                         * Zeitraum, Monteure und Adresse, und der Schritt
                         * folgt daraus. Zwei Wege in denselben Zustand
                         * liefen sonst auseinander.
                         */
                        if (naechster.key === 'montage') setMontageOffen(true);
                        else setSchrittFuer(b);
                      }}
                    >
                      {naechster.key === 'montage' ? 'Montage planen' : naechster.aktion}
                    </button>
                  )}
                  {!abgeschlossen && (
                    <button
                      type="button"
                      className="ab-stift"
                      title="Bestellung bearbeiten – Lieferant, Umfang, Liefertermin, Verantwortlicher"
                      onClick={() => setBearbeiten(b)}
                    >
                      <IconStift />
                    </button>
                  )}
                  {loeschbar && (
                    <button
                      type="button"
                      className="ab-knopf"
                      title="Nur möglich, solange kein Schritt quittiert ist"
                      disabled={entfernen.isPending}
                      onClick={() => entfernen.mutate(b.id)}
                    >
                      Entfernen
                    </button>
                  )}
                </div>
              </header>

              <footer className="ab-karte-fuss">
                <div className="ab-feld">
                  <span className="ab-feld-label">Liefertermin</span>
                  {b.lieferWoche || b.lieferDatum ? (
                    <>
                      <span className={`ab-feld-wert ab-termin ${b.lieferZustand === 'ueber' ? 'ueber' : ''}`}>
                        {b.lieferText}
                      </span>
                      <span className="ab-feld-unter">
                        {b.lieferDatum ? 'bestätigt vom Lieferanten' : 'Woche · wird mit der AB bestätigt'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="ab-feld-wert ab-leise">offen</span>
                      <span className="ab-feld-unter">kommt mit der unterfertigten AB</span>
                    </>
                  )}
                </div>
                {/*
                  Bei wem die Bestellung in diesem Schritt liegt (Mein Tag v6): die
                  Person hinter der Rolle laut Zuständigkeitsmatrix – Kalkulant oder
                  Versender –, oder die, der sie übergeben wurde. Klick übergibt.
                */}
                <div className="ab-feld">
                  <span className="ab-feld-label">Liegt bei</span>
                  <span className="ab-feld-wert ab-person">
                    <LiegtBei
                      lb={b.liegtBei}
                      onUebergeben={b.liegtBei ? () => setUebergabeFuer(b) : undefined}
                    />
                  </span>
                  <span className="ab-feld-unter">{b.zweck}</span>
                </div>
                <div className="ab-feld">
                  <span className="ab-feld-label">Zuletzt quittiert</span>
                  {letzter ? (
                    <>
                      <span className="ab-feld-wert">{letzter.schrittLang}</span>
                      <span className="ab-feld-unter">
                        {letzter.wochentag ? `${letzter.wochentag}, ` : ''}
                        {letzter.datumText} · {letzter.user}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="ab-feld-wert">Bestellung angelegt</span>
                      <span className="ab-feld-unter">
                        {[b.angelegtAmText, b.angelegtVon || 'aus Monday übernommen'].join(' · ')}
                      </span>
                    </>
                  )}
                </div>
              </footer>
            </article>
          );
        })}
      </div>

      {/* Service: ein eigener Vorgang am Auftrag, parallel zur Kette. */}
      <Serviceblock angebot={angebot} onFertig={onFertig} />

      {uebergabeFuer?.liegtBei && (
        <ZustaendigModal
          angebot={angebot}
          bestellungId={uebergabeFuer.id}
          liegtBei={uebergabeFuer.liegtBei}
          onSchliessen={() => setUebergabeFuer(null)}
          onFertig={() => {
            setUebergabeFuer(null);
            onFertig();
          }}
        />
      )}

      {/*
        Rechnungen aus sevDesk – gelesen, nicht geschrieben. Eine Zeile unter
        den Bestellungen: Als eigener Kasten darüber trennte sie Überschrift
        und Inhalt und wirkte wie ein Fehlerhinweis.
      */}
      <div className="ab-rechnungen-zeile">
        <span className="ab-kicker">Rechnungen aus sevDesk</span>
        {angebot.rechnungen.length === 0 ? (
          <span className="ab-rechnungen-text">
            {angebot.anzahlungAm
              ? `Anzahlung ${angebot.anzahlungBetrag ? `${angebot.anzahlungBetragText} ` : ''}eingegangen am ${angebot.anzahlungAmText} · keine Rechnung zugeordnet`
              : 'noch keine zu diesem Auftrag'}
          </span>
        ) : (
          <ul className="ab-rechnungsliste">
            {angebot.anzahlungAm && (
              <li className="ab-anzahlung">
                Anzahlung {angebot.anzahlungBetrag ? `${angebot.anzahlungBetragText} ` : ''}eingegangen am{' '}
                {angebot.anzahlungAmText}
              </li>
            )}
            {angebot.rechnungen.map((r) => (
              <li key={r.id} className={`ab-rechnung ${r.bezahlt ? 'bezahlt' : r.entwurf ? 'entwurf' : 'offen'}`}>
                <a className="ab-rechnung-nr" href={r.url} target="_blank" rel="noreferrer" title="In sevDesk öffnen">
                  {r.nummer || 'ohne Nummer'}
                </a>
                <span className="ab-rechnung-art">
                  {r.istAnzahlung ? 'Anzahlung' : r.artText} · {r.datumText}
                </span>
                <span className="ab-rechnung-betrag">{r.bruttoText}</span>
                <span className="ab-rechnung-status">{r.statusText}</span>
              </li>
            ))}
          </ul>
        )}
        {angebot.kunde.sevdeskUrl ? (
          <a className="ab-rechnungen-link" href={angebot.kunde.sevdeskUrl} target="_blank" rel="noreferrer">
            In sevDesk anlegen <span aria-hidden="true">↗</span>
          </a>
        ) : (
          <a
            className="ab-rechnungen-link"
            href="/kunden"
            title="Der Kunde ist noch nicht mit einem sevDesk-Kontakt verknüpft – in der Kundenstammverwaltung zuordnen"
          >
            Kunde mit sevDesk-Kontakt verknüpfen
          </a>
        )}
      </div>

      {montageOffen && (

        <MontageDialog

          einsatz={null}

          angebotId={angebot.id}

          onSchliessen={() => setMontageOffen(false)}

          onFertig={() => {

            setMontageOffen(false);

            onFertig();

          }}

        />

      )}


      {schrittFuer && (
        <SchrittModal
          bestellung={schrittFuer}
          angebotName={angebot.name}
          auftragFehlt={auftragFehlt}
          onSchliessen={() => setSchrittFuer(null)}
          onFertig={onFertig}
        />
      )}
      {bearbeiten && (
        <BestellungModal
          bestellung={bearbeiten}
          angebot={{ id: angebot.id, name: angebot.name, kalkulant: angebot.kalkulant }}
          onSchliessen={() => setBearbeiten(null)}
          onFertig={onFertig}
        />
      )}
      {neuOffen && (
        <BestellungModal
          bestellung={null}
          angebot={{ id: angebot.id, name: angebot.name, kalkulant: angebot.kalkulant }}
          onSchliessen={() => setNeuOffen(false)}
          onFertig={onFertig}
        />
      )}
    </div>
  );
}
